import React from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";
import Modal from "@material-ui/core/Modal";
import Fade from "@material-ui/core/Fade";
import Backdrop from "@material-ui/core/Backdrop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import KeyboardReturnIcon from "@material-ui/icons/KeyboardReturn";
import M_DataStateRenderer from "../../DataStateRenderer/M_DataStateRenderer";
import A_Skeleton from "../../../../Atoms/Feedback/Skeletons/A_Skeleton";
import M_SearchResultsList from "./M_SearchResultsList";
import { useStyles } from "./M_GlobalSearch.styles";
import {
  GLOBAL_SEARCH,
  SEARCH_STRINGS,
  MODIFIER_KEY,
  QUICK_LINK_GROUPS,
  SEARCH_KEYS,
  KEY_LABELS,
  SKELETON,
  SEARCH_UI_ICONS,
} from "./M_GlobalSearch.config";
import {
  fetchGlobalSearchResults,
  cancelGlobalSearch,
  buildGroups,
  buildTabs,
  flattenGroups,
  isSamePage,
  isSameCrm,
  getRecentSearches,
  addRecentSearch,
  clearRecentSearches,
  buildRecentGroup,
} from "./M_GlobalSearch.helper";

const M_GlobalSearch = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const inputRef = React.useRef(null);
  const bodyRef = React.useRef(null);

  const [keyword, setKeyword] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [recent, setRecent] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(GLOBAL_SEARCH.ALL_TAB);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const typed = keyword.trim().length;
  const hasQuery = typed >= GLOBAL_SEARCH.MIN_CHARS;
  const isTypingTooShort = typed > 0 && typed < GLOBAL_SEARCH.MIN_CHARS;
  const isAllTab = activeTab === GLOBAL_SEARCH.ALL_TAB;

  const groups = React.useMemo(() => buildGroups(results), [results]);
  const tabs = React.useMemo(() => buildTabs(groups), [groups]);
  const visibleGroups = React.useMemo(
    () =>
      isAllTab ? groups : groups.filter((group) => group.key === activeTab),
    [groups, activeTab, isAllTab]
  );
  const idleGroups = React.useMemo(
    () => [...buildRecentGroup(recent), ...QUICK_LINK_GROUPS],
    [recent]
  );
  const displayGroups = hasQuery
    ? visibleGroups
    : isTypingTooShort
    ? []
    : idleGroups;
  const flatItems = React.useMemo(
    () => flattenGroups(displayGroups),
    [displayGroups]
  );

  const executeSearch = React.useCallback(async (value) => {
    if (!value || value.length < GLOBAL_SEARCH.MIN_CHARS) {
      cancelGlobalSearch();
      setResults([]);
      setError(null);
      setLoading(false);
      setActiveIndex(0);
      return;
    }
    setLoading(true);
    setError(null);
    const response = await fetchGlobalSearchResults(value);
    if (response.cancelled) {
      return;
    }
    setLoading(false);
    if (response.error) {
      setResults([]);
      setError(SEARCH_STRINGS.ERROR);
      return;
    }
    setResults(response.data || []);
    setActiveTab(GLOBAL_SEARCH.ALL_TAB);
    setActiveIndex(0);
  }, []);

  const runSearch = React.useMemo(
    () => debounce(executeSearch, GLOBAL_SEARCH.DEBOUNCE_MS),
    [executeSearch]
  );

  React.useEffect(() => () => runSearch.cancel(), [runSearch]);

  React.useEffect(() => {
    if (open) {
      setKeyword("");
      setResults([]);
      setRecent(getRecentSearches());
      setError(null);
      setLoading(false);
      setActiveTab(GLOBAL_SEARCH.ALL_TAB);
      setActiveIndex(0);
      const focusTimer = setTimeout(
        () => inputRef.current?.focus(),
        GLOBAL_SEARCH.FOCUS_DELAY_MS
      );
      return () => clearTimeout(focusTimer);
    }
    cancelGlobalSearch();
    return undefined;
  }, [open]);

  React.useEffect(() => {
    const node = bodyRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    if (node) {
      node.scrollIntoView({ block: GLOBAL_SEARCH.SCROLL_BLOCK });
    }
  }, [activeIndex, displayGroups]);

  const handleSelect = (item) => {
    if (!item || !item.route) {
      return;
    }
    if (!item.isQuickLink) {
      setRecent(addRecentSearch(item));
    }
    onClose();
    const sameContext = item.isQuickLink
      ? isSamePage(item.route)
      : isSameCrm(item.route);
    if (sameContext) {
      window.location.assign(item.route);
      return;
    }
    history.push(item.route);
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    executeSearch(keyword.trim());
  };

  const handleClearRecent = () => {
    setRecent(clearRecentSearches());
    setActiveIndex(0);
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    const trimmed = value.trim();
    if (trimmed.length >= GLOBAL_SEARCH.MIN_CHARS) {
      setLoading(true);
      setError(null);
    } else {
      setLoading(false);
      setActiveIndex(0);
    }
    runSearch(trimmed);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setActiveIndex(0);
  };

  const tabsVisible = hasQuery && results.length > 0 && tabs.length > 1;

  const cycleTab = (direction) => {
    const currentIndex = tabs.findIndex((tab) => tab.key === activeTab);
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (safeIndex + direction + tabs.length) % tabs.length;
    handleTabChange(tabs[nextIndex].key);
  };

  const handleKeyDown = (event) => {
    if (event.key === SEARCH_KEYS.ARROW_DOWN) {
      event.preventDefault();
      setActiveIndex((prev) =>
        Math.min(prev + 1, Math.max(flatItems.length - 1, 0))
      );
    } else if (event.key === SEARCH_KEYS.ARROW_UP) {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === SEARCH_KEYS.ENTER) {
      event.preventDefault();
      handleSelect(flatItems[activeIndex]);
    } else if (event.key === SEARCH_KEYS.TAB && tabsVisible) {
      event.preventDefault();
      cycleTab(event.shiftKey ? -1 : 1);
    } else if (event.key === SEARCH_KEYS.ESCAPE) {
      onClose();
    }
  };

  const skeleton = (
    <div>
      {SKELETON.ROWS.map((row) => (
        <div className={classes.skeletonRow} key={row}>
          <A_Skeleton
            variant="rect"
            width={SKELETON.ICON_SIZE}
            height={SKELETON.ICON_SIZE}
          />
          <div style={{ flex: 1 }}>
            <A_Skeleton
              variant="text"
              width={SKELETON.TITLE_WIDTH}
              height={SKELETON.TITLE_HEIGHT}
            />
            <A_Skeleton
              variant="text"
              width={SKELETON.SUB_WIDTH}
              height={SKELETON.SUB_HEIGHT}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className={classes.state}>
      <FontAwesomeIcon icon={SEARCH_UI_ICONS.SEARCH} className={classes.stateIcon} />
      <div className={classes.stateTitle}>{SEARCH_STRINGS.NO_RESULTS}</div>
      <div className={classes.stateHint}>{SEARCH_STRINGS.NO_RESULTS_HINT}</div>
    </div>
  );

  const minCharState = (
    <div className={classes.state}>
      <FontAwesomeIcon icon={SEARCH_UI_ICONS.SEARCH} className={classes.stateIcon} />
      <div className={classes.stateHint}>{SEARCH_STRINGS.MIN_CHARS_HINT}</div>
    </div>
  );

  const errorState = (
    <div className={classes.state}>
      <FontAwesomeIcon icon={SEARCH_UI_ICONS.ERROR} className={classes.stateIcon} />
      <div className={classes.stateTitle}>{SEARCH_STRINGS.ERROR}</div>
      <button type="button" className={classes.retryButton} onClick={handleRetry}>
        <FontAwesomeIcon icon={SEARCH_UI_ICONS.RETRY} />
        {SEARCH_STRINGS.RETRY}
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 200, className: classes.backdrop }}
    >
      <Fade in={open}>
        <div className={classes.palette} onKeyDown={handleKeyDown}>
          <div className={classes.header}>
            <span className={classes.headerIcon}>
              <FontAwesomeIcon icon={SEARCH_UI_ICONS.SEARCH} />
            </span>
            <input
              ref={inputRef}
              className={classes.input}
              placeholder={SEARCH_STRINGS.PLACEHOLDER}
              value={keyword}
              onChange={handleChange}
              aria-label={SEARCH_STRINGS.PLACEHOLDER}
            />
            <span className={classes.kbd}>{KEY_LABELS.ESCAPE}</span>
          </div>

          {hasQuery && results.length > 0 && (
            <div className={classes.tabs}>
              {tabs.map((tab) => {
                const isActive = tab.key === activeTab;
                return (
                  <button
                    type="button"
                    key={tab.key}
                    className={`${classes.tab} ${isActive ? classes.tabActive : ""}`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {tab.label}
                    <span
                      className={`${classes.tabCount} ${
                        isActive ? "" : classes.tabCountIdle
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className={classes.body} ref={bodyRef}>
            {!hasQuery ? (
              isTypingTooShort ? (
                minCharState
              ) : (
                <M_SearchResultsList
                  groups={idleGroups}
                  showGroupLabel
                  showTypeChip={false}
                  activeIndex={activeIndex}
                  query=""
                  onSelect={handleSelect}
                  onActivate={setActiveIndex}
                  onClearRecent={recent.length ? handleClearRecent : undefined}
                />
              )
            ) : error ? (
              errorState
            ) : (
              <M_DataStateRenderer
                loading={loading}
                skeleton={skeleton}
                isEmpty={!loading && results.length === 0}
                empty={emptyState}
              >
                <M_SearchResultsList
                  groups={visibleGroups}
                  showGroupLabel={isAllTab}
                  showTypeChip={false}
                  activeIndex={activeIndex}
                  query={keyword.trim()}
                  onSelect={handleSelect}
                  onActivate={setActiveIndex}
                />
              </M_DataStateRenderer>
            )}
          </div>

          <div className={classes.footer}>
            <span className={classes.footerItem}>
              <span className={classes.kbd}>
                <FontAwesomeIcon icon={SEARCH_UI_ICONS.NAV_UP} />
              </span>
              <span className={classes.kbd}>
                <FontAwesomeIcon icon={SEARCH_UI_ICONS.NAV_DOWN} />
              </span>
              {SEARCH_STRINGS.NAVIGATE}
            </span>
            <span className={classes.footerItem}>
              <span className={classes.kbd} aria-hidden="true">
                <KeyboardReturnIcon fontSize="inherit" />
              </span>
              {SEARCH_STRINGS.OPEN}
            </span>
            {tabsVisible && (
              <span className={classes.footerItem}>
                <span className={classes.kbd}>{KEY_LABELS.TAB}</span>
                {SEARCH_STRINGS.SWITCH}
              </span>
            )}
            <span className={classes.footerItem}>
              <span className={classes.kbd}>{MODIFIER_KEY}</span>
              <span className={classes.kbd}>{KEY_LABELS.HOTKEY}</span>
              {SEARCH_STRINGS.CLOSE}
            </span>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

M_GlobalSearch.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default M_GlobalSearch;
